import React, { useMemo } from 'react';
import { Padding } from '../../attributes/Padding';
import { BackgroundColor } from '../../attributes/BackgroundColor';
import { Color } from '../../attributes/Color';
import { Link } from '../../attributes/Link';
import { Width } from '../../attributes/Width';
import { ContainerBackgroundColor } from '../../attributes/ContainerBackgroundColor';
import { Align } from '../../attributes/Align';
import { FontSize } from '../../attributes/FontSize';
import { FontStyle } from '../../attributes/FontStyle';
import { FontWeight } from '../../attributes/FontWeight';
import { FontFamily } from '../../attributes/FontFamily';
import { TextDecoration } from '../../attributes/TextDecoration';
import { LineHeight } from '../../attributes/LineHeight';
import { LetterSpacing } from '../../attributes/LetterSpacing';
import { TextTransform } from '../../attributes/TextTransform';
import { TextWrap } from '../../attributes/TextWrap';
import { BorderWidth } from '../../attributes/BorderWidth';
import { BorderStyle } from '../../attributes/BorderStyle';
import { BorderColor } from '../../attributes/BorderColor';
import { Collapse, Grid } from '@arco-design/web-react';
import { InputWithUnitField, TextField } from '../../../../components/Form';
import { TextStyle, useFocusIdx } from '@bay-email/editor';
import { AttributesPanelWrapper } from '../../attributes/AttributesPanelWrapper';
import { ClassName } from '../../attributes/ClassName';
import { CollapseWrapper } from '../../attributes/CollapseWrapper';
import { t } from '@bay-email/core';
import { pixelAdapter } from '../../adapter';
import { useSelectedNode } from '@extensions/hooks/useSelectedNode';
import { AttachedListingSection } from '../shared/AttachedListingSection';
import { usesListingMergeTags } from '../shared/listingAttachment';
import { useClearAttachedListingWhenUnused } from '../shared/useClearAttachedListingWhenUnused';

export function Button() {
	const { focusIdx } = useFocusIdx();
	const { selectedNode } = useSelectedNode();
	const hrefValue = String(selectedNode?.attributes?.href || '');
	const showListingSettings = useMemo(
		() => usesListingMergeTags(hrefValue),
		[hrefValue],
	);
	useClearAttachedListingWhenUnused(selectedNode, showListingSettings);
	const defaultActiveKey = useMemo(
		() => (showListingSettings ? ['-2', '-1', '0', '1'] : ['-1', '0', '1']),
		[showListingSettings],
	);

	return (
		<CollapseWrapper
			isChevron
			iconPosition='right'
			defaultActiveKey={defaultActiveKey}
			style={{ padding: 0 }}
		>
			{showListingSettings && (
				<Collapse.Item
					contentStyle={{ padding: 0 }}
					name='-2'
					header={t('Listing Settings')}
				>
					<AttachedListingSection observedValues={[hrefValue]} />
				</Collapse.Item>
			)}
			<Collapse.Item
				contentStyle={{ padding: 0 }}
				name='-1'
				header={t('Content Block Settings')}
			>
				<div className='p-3'>
					<TextField
						label={t('Button text')}
						name={`${focusIdx}.data.value.content`}
						placeholder={t('Enter button text')}
					/>

					<Link
						context='button-link'
						isNewDynamicField
					/>
				</div>
			</Collapse.Item>

			<Collapse.Item
				contentStyle={{ padding: 0 }}
				name='0'
				header={t('Dimension')}
			>
				<div className='p-3'>
					<Grid.Row
						gutter={16}
						style={{ marginBottom: '16px' }}
					>
						<Grid.Col span={12}>
							<div style={{ paddingRight: '8px' }}>
								<Width
									autoEnabled
									config={pixelAdapter}
								/>
							</div>
						</Grid.Col>
					</Grid.Row>
					<Padding
						title={t('Padding')}
						attributeName='padding'
						showResetAll
					/>
					<Padding
						title={t('Inner padding')}
						attributeName='inner-padding'
					/>
				</div>
			</Collapse.Item>

			<Collapse.Item
				contentStyle={{ padding: 0 }}
				name='1'
				header={t('Styling')}
			>
				<div className='p-3'>
					{/* Color inputs in two-column layout */}
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<Color title={t('Text color')} />
						</Grid.Col>
						<Grid.Col span={12}>
							<BackgroundColor title={t('Button color')} />
						</Grid.Col>
					</Grid.Row>
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<ContainerBackgroundColor title={t('Background color')} />
						</Grid.Col>
					</Grid.Row>

					{/* Font Family - Full Width */}
					<FontFamily />

					{/* Typography inputs in two-column layout */}
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<FontSize />
						</Grid.Col>
						<Grid.Col span={12}>
							<LetterSpacing
								label={t('Letter Spacing (px)')}
								config={pixelAdapter}
							/>
						</Grid.Col>
					</Grid.Row>

					<LineHeight />

					<FontWeight />

					{/* Radio button groups - Full Width */}
					<FontStyle />
					<TextDecoration />
					<Align />
					<TextTransform />
					<TextWrap />

					{/* Border Controls */}
					<TextStyle
						variation='strong'
						style={{ marginBottom: '12px', marginTop: '16px', display: 'block' }}
					>
						{t('Border')}
					</TextStyle>
					<Grid.Row gutter={16}>
						<Grid.Col span={10}>
							<BorderColor secondary />
						</Grid.Col>
						<Grid.Col span={8}>
							<BorderWidth
								secondary
								config={pixelAdapter}
								label={t('Thickness (px)')}
							/>
						</Grid.Col>
						<Grid.Col span={6}>
							<BorderStyle secondary />
						</Grid.Col>
					</Grid.Row>
					<Grid.Row gutter={16}>
						<Grid.Col span={12}>
							<InputWithUnitField
								label={`${t('Border Radius')} (px)`}
								name={`${focusIdx}.attributes.border-radius`}
								config={pixelAdapter}
							/>
						</Grid.Col>
					</Grid.Row>
				</div>
			</Collapse.Item>

			<Collapse.Item
				contentStyle={{ padding: 0 }}
				name='4'
				header={t('Extra')}
			>
				<div className='p-3'>
					<ClassName />
				</div>
			</Collapse.Item>
		</CollapseWrapper>
	);
}
